import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, Input, OnInit } from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ShopBanner, ShopSection, ShopSettings } from '@infrab4a/connect';
import { MessageService } from 'primeng/api';
import { ImageUploadComponent } from 'src/app/components/image-upload/image-upload.component';
import { ShopSettingsService } from 'src/app/connect-api/api/shop-settings/shop-settings.service';
import { LoaderService } from 'src/app/services/loader.service';
import {
  MENSMARKET_HOME_BANNERS_PATH,
  MENSMARKET_HOME_DOCUMENT_ID,
  MENSMARKET_HOME_ROUTE
} from '../mens-new-home.constants';

export type DisplayRules = {
  subscriberBuyer: boolean;
  notSubscriber: boolean;
  subscriberNotBuyer: boolean;
  topBadgeSubscriber: boolean;
  beautyProfile: boolean;
};

export type Banner = {
  mobileImage: string;
  desktopImage: string;
  link: string;
  altText: string;
  countdown?: string;
  displayRules?: Array<Partial<DisplayRules>>;
  publishDate?: Date;
  expirationDate?: Date;
};

export type Carousel = {
  type: 'CAROUSEL';
  fixed: boolean;
  editable: boolean;
  banners: Banner[];
};

@Component({
  selector: 'app-mens-new-home-carousel-form',
  templateUrl: './carousel-form.component.html',
  styleUrl: './carousel-form.component.scss'
})
export class MensNewHomeCarouselFormComponent
  extends ImageUploadComponent<any>
  implements OnInit
{
  @Input() id: string | null = null;
  fb = inject(FormBuilder);
  override form: FormGroup = new FormGroup({
    id: new FormControl<string>(''),
    type: new FormControl<string>('CAROUSEL'),
    fixed: new FormControl<boolean>(false),
    editable: new FormControl<boolean>(true),
    banners: this.fb.array([])
  });

  displayRuleOptions = [
    { label: 'Não se aplica', value: null },
    { label: 'Sim', value: true },
    { label: 'Não', value: false }
  ];

  settings: Partial<ShopSettings> & { sections?: ShopSection[] };
  private messageService = inject(MessageService);
  private shopSettingsService = inject(ShopSettingsService);
  private activatedRoute = inject(ActivatedRoute);

  async ngOnInit(): Promise<void> {
    this.settings = await this.shopSettingsService.getById(MENSMARKET_HOME_DOCUMENT_ID);
    this.id = this.activatedRoute.snapshot.paramMap.get('id');
    if (this.id) {
      const section = this.settings.sections?.find((s) => s.id === this.id);
      if (section) {
        this.form.patchValue({ ...section, banners: undefined });

        if ((section as Carousel).banners) {
          section.banners.forEach((banner) => {
            const bannerGroup = this.fb.group({
              mobileImage: [banner.mobileImage, Validators.required],
              desktopImage: [banner.desktopImage, Validators.required],
              link: [banner.link],
              altText: [banner.altText, Validators.required],
              countdown: [banner.countdown || ''],
              publishDate: [banner.publishDate || null],
              expirationDate: [banner.expirationDate || null],
              displayRules: this.fb.group({
                subscriberBuyer: [null],
                notSubscriber: [null],
                subscriberNotBuyer: [null],
                topBadgeSubscriber: [null],
                beautyProfile: [null]
              })
            });

            if (banner.displayRules && Array.isArray(banner.displayRules)) {
              for (const rule of banner.displayRules) {
                for (const key in rule) {
                  if (bannerGroup.get('displayRules')?.get(key)) {
                    bannerGroup
                      .get('displayRules')
                      ?.get(key)
                      ?.setValue(rule[key]);
                  }
                }
              }
            }

            this.banners.push(bannerGroup);
          });
        }
      }
    }

    console.log('this.settings', this.settings);
  }

  get banners(): FormArray {
    return this.form.get('banners') as FormArray;
  }

  addBanner(): void {
    const banner = this.fb.group({
      id: [crypto.randomUUID()],
      mobileImage: ['', Validators.required],
      desktopImage: ['', Validators.required],
      link: ['', Validators.required],
      altText: ['', Validators.required],
      countdown: [''],
      publishDate: [null],
      expirationDate: [null],
      displayRules: new FormGroup({
        subscriberBuyer: new FormControl(null),
        notSubscriber: new FormControl(null),
        subscriberNotBuyer: new FormControl(null),
        topBadgeSubscriber: new FormControl(null),
        beautyProfile: new FormControl(null)
      })
    });
    this.banners.push(banner);
  }

  removeBanner(index: number): void {
    this.banners.removeAt(index);
  }

  onDrop(event: CdkDragDrop<any[]>): void {
    moveItemInArray(
      this.banners.controls,
      event.previousIndex,
      event.currentIndex
    );
    this.banners.updateValueAndValidity();
  }

  async onSubmit(): Promise<void> {
    if (this.form.valid) {
      LoaderService.showLoader();
      const newSection = this.getCarouselInfo();
      const sections = this.settings.sections || [];
      const existingIndex = sections.findIndex((s) => s.id === this.id);

      if (existingIndex >= 0) {
        sections[existingIndex] = newSection;
      } else {
        sections.push(newSection);
      }

      await this.shopSettingsService.update({
        id: this.settings.id,
        sections: sections
      });

      await this.afterSubmit();
    }
    LoaderService.showLoader(false);
    this.messageService.add({
      severity: 'success',
      summary: 'Sucesso',
      detail: 'Carrossel salvo com sucesso'
    });

    this.router.navigate([MENSMARKET_HOME_ROUTE]);
  }

  private getCarouselInfo() {
    const formValue = this.form.value;
    const banners = formValue.banners.map((banner: any) => {
      const displayRules = [];
      const rules = banner.displayRules;
      for (const key in rules) {
        if (rules[key] !== null) {
          displayRules.push({ [key]: rules[key] });
        }
      }
      return {
        mobileImage: banner.mobileImage,
        desktopImage: banner.desktopImage,
        link: banner.link,
        altText: banner.altText,
        countdown: banner.countdown || undefined,
        displayRules: displayRules.length > 0 ? displayRules : [],
        publishDate: banner.publishDate || undefined,
        expirationDate: banner.expirationDate || undefined
      };
    });

    return {
      id: formValue.id || crypto.randomUUID(),
      type: formValue.type,
      fixed: formValue.fixed,
      editable: formValue.editable,
      banners
    };
  }

  get mobileImageWidth(): number {
    return 690;
  }

  get mobileImageHeight(): number {
    return 350;
  }

  get desktopImageWidth(): number {
    return 1800;
  }

  get desktopImageHeight(): number {
    return 500;
  }

  override get filePath(): string {
    return MENSMARKET_HOME_BANNERS_PATH;
  }

  override get model(): ShopBanner {
    return null;
  }

  override get modelId(): string | number {
    return crypto.randomUUID();
  }
}
